/**
 * ExperimentCard — Displays a single AI Brief with role-appropriate actions.
 *
 * Props:
 *   experiment     { id, experiment_name, is_active, project_info, survey_count, team_id, team_name }
 *   userRole       'admin' | 'user' | 'viewer'
 *   userSurveyId   number | null — the current user's existing survey id for this experiment, if any
 *   onViewBrief    fn(experiment) — all roles
 *   onTakeSurvey   fn(experiment)  — user/admin, active only
 *   onViewResults  fn(experiment)
 *   onEdit         fn(experiment)  — admin only
 *   onToggleActive fn(experiment)  — admin only (deactivate or reactivate)
 */
export default function ExperimentCard({
  experiment,
  userRole,
  userSurveyId,
  onViewBrief,
  onTakeSurvey,
  onViewResults,
  onEdit,
  onToggleActive,
}) {
  const isAdmin = userRole === 'admin'
  const canTakeSurvey = (userRole === 'admin' || userRole === 'user') && experiment.is_active
  const teamColorIndex = experiment.team_id != null ? ((experiment.team_id - 1) % 8) + 1 : 0

  return (
    <div
      className={`experiment-card${experiment.is_active ? '' : ' inactive'}`}
      data-team-color={teamColorIndex}
      style={{ cursor: 'pointer' }}
      onClick={() => onViewBrief(experiment)}
    >
      <span
        className={`status-dot${experiment.is_active ? ' status-dot--active' : ' status-dot--inactive'}`}
        title={experiment.is_active ? 'Active' : 'Inactive'}
        aria-label={experiment.is_active ? 'Active' : 'Inactive'}
      />

      {userSurveyId && (
        <span
          className={`experiment-card-survey-badge${experiment.is_active ? '' : ' experiment-card-survey-badge--inactive'}`}
          title="You've submitted a survey for this AI Brief"
        >
          <CheckCircleIcon />
          Submitted
        </span>
      )}

      {experiment.team_name && (
        <span className="experiment-card-team-badge" title={experiment.team_name}>
          {experiment.team_name}
        </span>
      )}

      <p
        className="experiment-card-name"
        title={experiment.experiment_name}
      >
        {experiment.experiment_name}
      </p>

      {experiment.project_info && (
        <p
          className="experiment-card-meta"
          title={experiment.project_info}
        >
          {experiment.project_info}
        </p>
      )}

      <p className="experiment-card-count">
        {experiment.survey_count ?? 0} survey{experiment.survey_count !== 1 ? 's' : ''} submitted
      </p>

      <p className="experiment-card-updated">
        Updated {formatDate(experiment.updated_at ?? experiment.created_at)}
      </p>

      <div className="experiment-card-actions">
        {/* View AI Brief — all roles */}
        <button
          type="button"
          className="icon-btn"
          data-tooltip="View AI Brief"
          aria-label="View AI Brief"
          onClick={(e) => { e.stopPropagation(); onViewBrief(experiment) }}
        >
          <EyeIcon />
        </button>

        {/* Take / Edit Survey — user & admin, active only */}
        {canTakeSurvey && (
          <button
            type="button"
            className="icon-btn icon-btn--primary"
            data-tooltip={userSurveyId ? 'Edit Survey' : 'Take Survey'}
            aria-label={userSurveyId ? 'Edit Survey' : 'Take Survey'}
            onClick={(e) => { e.stopPropagation(); onTakeSurvey(experiment) }}
          >
            <ClipboardPenIcon />
          </button>
        )}

        {/* View Results — all roles */}
        <button
          type="button"
          className="icon-btn"
          data-tooltip="View Results"
          aria-label="View Results"
          onClick={(e) => { e.stopPropagation(); onViewResults(experiment) }}
        >
          <BarChartIcon />
        </button>

        {/* Edit AI Brief — admin only */}
        {isAdmin && (
          <button
            type="button"
            className="icon-btn"
            data-tooltip="Edit AI Brief"
            aria-label="Edit AI Brief"
            onClick={(e) => { e.stopPropagation(); onEdit(experiment) }}
          >
            <PencilIcon />
          </button>
        )}

        {/* Deactivate / Reactivate — admin only, pushed to far right */}
        {isAdmin && (
          experiment.is_active ? (
            <button
              type="button"
              className="icon-btn icon-btn--danger"
              style={{ marginLeft: 'auto' }}
              data-tooltip="Deactivate AI Brief"
              aria-label="Deactivate AI Brief"
              onClick={(e) => { e.stopPropagation(); onToggleActive(experiment) }}
            >
              <TrashIcon />
            </button>
          ) : (
            <button
              type="button"
              className="icon-btn icon-btn--success"
              style={{ marginLeft: 'auto' }}
              data-tooltip="Reactivate"
              aria-label="Reactivate AI Brief"
              onClick={(e) => { e.stopPropagation(); onToggleActive(experiment) }}
            >
              <ArrowPathIcon />
            </button>
          )
        )}
      </div>
    </div>
  )
}

/* ── Helpers ─────────────────────────────────────────── */

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/* ── Inline SVG Icons ────────────────────────────────── */

function EyeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  )
}

function ClipboardPenIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2h6a1 1 0 0 1 1 1v1H8V3a1 1 0 0 1 1-1z"/>
      <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/>
      <path d="M17.5 3.5a2.121 2.121 0 0 1 3 3L14 13l-4 1 1-4 6.5-6.5z"/>
    </svg>
  )
}

function BarChartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="12" width="4" height="9" rx="1"/>
      <rect x="10" y="7" width="4" height="14" rx="1"/>
      <rect x="17" y="3" width="4" height="18" rx="1"/>
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/>
      <path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  )
}

function ArrowPathIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4v5h5"/>
      <path d="M20 20v-5h-5"/>
      <path d="M20 9A8 8 0 0 0 5.07 5.07"/>
      <path d="M4 15a8 8 0 0 0 14.93 3.93"/>
    </svg>
  )
}

function CheckCircleIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  )
}
